/**
 * System Translations for Forms
 *
 * Contains translations for:
 * - UI buttons and labels
 * - Validation error messages
 * - Common form field labels
 * - Status messages
 *
 * To add a new language: add a new key (e.g., 'ru') with all the same keys as 'en'.
 * The t() function will fall back to English for any missing key.
 */

export const systemTranslations = {
  en: {
    // UI Buttons
    submit: 'Submit',
    submitting: 'Submitting...',
    cancel: 'Cancel',
    save: 'Save',
    saving: 'Saving...',
    next: 'Next',
    previous: 'Previous',
    back: 'Back',
    clear: 'Clear',
    reset: 'Reset',
    upload: 'Upload',
    uploading: 'Uploading...',
    browse: 'Browse',
    selectFile: 'Select File',
    dragDropFile: 'Drag and drop file here or click to browse',

    // Validation Messages
    required: 'This field is required',
    invalidEmail: 'Please enter a valid email address',
    invalidPhone: 'Please enter a valid phone number',
    invalidDate: 'Please enter a valid date',
    invalidNumber: 'Please enter a valid number',
    minLength: 'Must be at least {min} characters',
    maxLength: 'Must be no more than {max} characters',
    minValue: 'Must be at least {min}',
    maxValue: 'Must be no more than {max}',
    pattern: 'Please enter a valid value',
    invalidZip: 'Please enter a valid ZIP code',
    invalidSSN: 'Please enter a valid SSN',

    // Headings of the blocked-submit toast (libraries/validation-toast-message.js).
    // "check" rather than "complete": a format failure is not an empty field.
    checkThisField: 'Please check this field:',
    checkTheseFields: 'Please check the highlighted fields:',

    // Common Field Labels (fallbacks)
    firstName: 'First Name',
    lastName: 'Last Name',
    middleName: 'Middle Name',
    fullName: 'Full Name',
    email: 'Email',
    phone: 'Phone',
    cellPhone: 'Cell Phone',
    homePhone: 'Home Phone',
    workPhone: 'Work Phone',
    address: 'Address',
    address1: 'Address Line 1',
    address2: 'Address Line 2',
    city: 'City',
    state: 'State',
    zip: 'ZIP Code',
    zipCode: 'ZIP Code',
    country: 'Country',
    dateOfBirth: 'Date of Birth',
    dob: 'Date of Birth',
    ssn: 'Social Security Number',
    gender: 'Gender',
    male: 'Male',
    female: 'Female',
    other: 'Other',
    preferNotToSay: 'Prefer not to say',
    signature: 'Signature',
    date: 'Date',
    comments: 'Comments',
    notes: 'Notes',

    // Status Messages
    formSubmitted: 'Form submitted successfully',
    formSaved: 'Form saved successfully',
    submissionError: 'Error submitting form. Please try again.',
    loadingForm: 'Loading form...',
    pleaseWait: 'Please wait...',

    // Signature
    signHere: 'Sign here',
    clearSignature: 'Clear signature',
    signatureRequired: 'Signature is required',

    // File Upload
    fileUploaded: 'File uploaded successfully',
    fileUploadError: 'Error uploading file',
    fileTooLarge: 'File is too large',
    invalidFileType: 'Invalid file type',
    maxFileSize: 'Maximum file size: {size}',

    // Yes/No
    yes: 'Yes',
    no: 'No',

    // Language
    language: 'Language',
    english: 'English',
    spanish: 'Spanish',

    // Form packets (one link, several forms — see libraries/packet-progress.js).
    // packetName is NEVER passed through t(): the interpolation below is String.replace, which
    // treats `$&`/`$1` in a practice's packet name as capture-group syntax. Render it as its own node.
    saveAndContinue: 'Save & Continue',
    sectionOf: 'Section {step} of {total}',
    lastSection: 'last one',
    packetCompleteTitle: "You've already completed this packet",
    packetCompleteMessage: "Every section has been submitted. There's nothing more to fill out.",
    packetCompleteHint: 'If you need to change anything, please contact the office.',

    // Break points inside ONE form (libraries/form-parts.js). The noun is "Part", never "Section":
    // the packet strip already says "Section X of Y" for whole forms, and the two can stack.
    // Reuses `back`, `saving` and `saveAndContinue` above.
    continue: 'Continue',
    partOf: 'Part {step} of {total}',
    progressSaveFailed: "We couldn't save your progress, but you can keep going.",

    // Health history by category — the 5th Open Dental data type
    // (custom-controls/opendentaldata/DiseasesCategorizedField.vue).
    // Only these FIXED strings are translated: the category names and the "No conditions" caption
    // are the office's own wording (per-language config inputs), and the condition names come
    // straight from Open Dental, which never translates them.
    // whichApply follows "**Category**: ", hence the lower-case opening word.
    whichApply: 'which of these apply to you? Check all that apply.',
    alsoOnFile: 'Also on file (not shown to patient)',
    noConditions: 'No conditions',
    // The required box under a ticked "Other" — its text is saved as Open Dental's Other Problems.
    describeOther: 'Please describe your other condition(s)',
    // The OPTIONAL box, under a category that also lists other conditions (typing selects "Other").
    describeOtherOptional: 'Any other condition(s) not listed above? (optional)',
    describeOtherPlaceholder: 'Type the condition(s) not listed above',
    describeOtherRequired: 'Please describe your other condition(s) before continuing.',

    // Post-op after-care link (ViewForm postop mode + PostOpAckBar.vue). No patient field is ever interpolated into these.
    postOpEyebrow: 'After-care instructions',
    postOpAckButton: "I've read these instructions",
    postOpAckSending: 'Sending...',
    postOpAckThanks: 'Thanks — the office has been notified. You can open this link again anytime.',
    postOpAckRetry: 'Something went wrong — please tap again.',
    postOpAckUnavailable: "We couldn't record that. If you have questions, please call the office."
  },

  es: {
    // UI Buttons
    submit: 'Enviar',
    submitting: 'Enviando...',
    cancel: 'Cancelar',
    save: 'Guardar',
    saving: 'Guardando...',
    next: 'Siguiente',
    previous: 'Anterior',
    back: 'Atrás',
    clear: 'Borrar',
    reset: 'Restablecer',
    upload: 'Subir',
    uploading: 'Subiendo...',
    browse: 'Examinar',
    selectFile: 'Seleccionar archivo',
    dragDropFile: 'Arrastre y suelte el archivo aquí o haga clic para examinar',

    // Validation Messages
    required: 'Este campo es requerido',
    invalidEmail: 'Ingrese una dirección de correo electrónico válida',
    invalidPhone: 'Ingrese un número de teléfono válido',
    invalidDate: 'Ingrese una fecha válida',
    invalidNumber: 'Ingrese un número válido',
    minLength: 'Debe tener al menos {min} caracteres',
    maxLength: 'Debe tener no más de {max} caracteres',
    minValue: 'Debe ser al menos {min}',
    maxValue: 'Debe ser no más de {max}',
    pattern: 'Ingrese un valor válido',
    invalidZip: 'Ingrese un código postal válido',
    invalidSSN: 'Ingrese un número de seguro social válido',
    checkThisField: 'Por favor revise este campo:',
    checkTheseFields: 'Por favor revise los campos marcados:',

    // Common Field Labels (fallbacks)
    firstName: 'Nombre',
    lastName: 'Apellido',
    middleName: 'Segundo nombre',
    fullName: 'Nombre completo',
    email: 'Correo electrónico',
    phone: 'Teléfono',
    cellPhone: 'Teléfono celular',
    homePhone: 'Teléfono de casa',
    workPhone: 'Teléfono del trabajo',
    address: 'Dirección',
    address1: 'Dirección línea 1',
    address2: 'Dirección línea 2',
    city: 'Ciudad',
    state: 'Estado',
    zip: 'Código postal',
    zipCode: 'Código postal',
    country: 'País',
    dateOfBirth: 'Fecha de nacimiento',
    dob: 'Fecha de nacimiento',
    ssn: 'Número de seguro social',
    gender: 'Género',
    male: 'Masculino',
    female: 'Femenino',
    other: 'Otro',
    preferNotToSay: 'Prefiero no decir',
    signature: 'Firma',
    date: 'Fecha',
    comments: 'Comentarios',
    notes: 'Notas',

    // Status Messages
    formSubmitted: 'Formulario enviado con éxito',
    formSaved: 'Formulario guardado con éxito',
    submissionError: 'Error al enviar el formulario. Por favor intente de nuevo.',
    loadingForm: 'Cargando formulario...',
    pleaseWait: 'Por favor espere...',

    // Signature
    signHere: 'Firme aquí',
    clearSignature: 'Borrar firma',
    signatureRequired: 'La firma es requerida',

    // File Upload
    fileUploaded: 'Archivo subido con éxito',
    fileUploadError: 'Error al subir el archivo',
    fileTooLarge: 'El archivo es demasiado grande',
    invalidFileType: 'Tipo de archivo no válido',
    maxFileSize: 'Tamaño máximo de archivo: {size}',

    // Yes/No
    yes: 'Sí',
    no: 'No',

    // Language
    language: 'Idioma',
    english: 'Inglés',
    spanish: 'Español',

    // Form packets
    saveAndContinue: 'Guardar y continuar',
    sectionOf: 'Sección {step} de {total}',
    lastSection: 'última',
    packetCompleteTitle: 'Ya completó este paquete',
    packetCompleteMessage: 'Todas las secciones han sido enviadas. No hay nada más que completar.',
    packetCompleteHint: 'Si necesita cambiar algo, comuníquese con la oficina.',

    // Break points inside one form
    continue: 'Continuar',
    partOf: 'Parte {step} de {total}',
    progressSaveFailed: 'No pudimos guardar su progreso, pero puede continuar.',

    // Health history by category
    whichApply: '¿cuáles de estos le aplican? Marque todos los que correspondan.',
    alsoOnFile: 'También en el expediente (no se muestra al paciente)',
    noConditions: 'Ninguna afección',
    describeOther: 'Describa sus otras afecciones',
    describeOtherOptional: '¿Alguna otra afección que no aparece arriba? (opcional)',
    describeOtherPlaceholder: 'Escriba las afecciones que no aparecen arriba',
    describeOtherRequired: 'Describa sus otras afecciones antes de continuar.',

    // Enlace de instrucciones posoperatorias
    postOpEyebrow: 'Instrucciones de cuidado posterior',
    postOpAckButton: 'He leído estas instrucciones',
    postOpAckSending: 'Enviando...',
    postOpAckThanks: 'Gracias: se ha notificado a la oficina. Puede abrir este enlace de nuevo cuando quiera.',
    postOpAckRetry: 'Algo salió mal. Por favor, toque de nuevo.',
    postOpAckUnavailable: 'No pudimos registrar esto. Si tiene preguntas, llame a la oficina.'
  },

  ru: {
    // UI Buttons
    submit: '\u041e\u0442\u043f\u0440\u0430\u0432\u0438\u0442\u044c',
    submitting: '\u041e\u0442\u043f\u0440\u0430\u0432\u043a\u0430...',
    cancel: '\u041e\u0442\u043c\u0435\u043d\u0430',
    save: '\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c',
    saving: '\u0421\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u0438\u0435...',
    next: '\u0414\u0430\u043b\u0435\u0435',
    previous: '\u041d\u0430\u0437\u0430\u0434',
    back: '\u041d\u0430\u0437\u0430\u0434',
    clear: '\u041e\u0447\u0438\u0441\u0442\u0438\u0442\u044c',
    reset: '\u0421\u0431\u0440\u043e\u0441\u0438\u0442\u044c',
    upload: '\u0417\u0430\u0433\u0440\u0443\u0437\u0438\u0442\u044c',
    uploading: '\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430...',
    browse: '\u041e\u0431\u0437\u043e\u0440',
    selectFile: '\u0412\u044b\u0431\u0440\u0430\u0442\u044c \u0444\u0430\u0439\u043b',
    dragDropFile: '\u041f\u0435\u0440\u0435\u0442\u0430\u0449\u0438\u0442\u0435 \u0444\u0430\u0439\u043b \u0441\u044e\u0434\u0430 \u0438\u043b\u0438 \u043d\u0430\u0436\u043c\u0438\u0442\u0435 \u0434\u043b\u044f \u0432\u044b\u0431\u043e\u0440\u0430',

    // Validation Messages
    required: '\u042d\u0442\u043e \u043f\u043e\u043b\u0435 \u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u043e',
    invalidEmail: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u044b\u0439 \u0430\u0434\u0440\u0435\u0441 \u044d\u043b\u0435\u043a\u0442\u0440\u043e\u043d\u043d\u043e\u0439 \u043f\u043e\u0447\u0442\u044b',
    invalidPhone: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u044b\u0439 \u043d\u043e\u043c\u0435\u0440 \u0442\u0435\u043b\u0435\u0444\u043e\u043d\u0430',
    invalidDate: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u0443\u044e \u0434\u0430\u0442\u0443',
    invalidNumber: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u043e\u0435 \u0447\u0438\u0441\u043b\u043e',
    minLength: '\u041c\u0438\u043d\u0438\u043c\u0443\u043c {min} \u0441\u0438\u043c\u0432\u043e\u043b\u043e\u0432',
    maxLength: '\u041c\u0430\u043a\u0441\u0438\u043c\u0443\u043c {max} \u0441\u0438\u043c\u0432\u043e\u043b\u043e\u0432',
    minValue: '\u041c\u0438\u043d\u0438\u043c\u0430\u043b\u044c\u043d\u043e\u0435 \u0437\u043d\u0430\u0447\u0435\u043d\u0438\u0435: {min}',
    maxValue: '\u041c\u0430\u043a\u0441\u0438\u043c\u0430\u043b\u044c\u043d\u043e\u0435 \u0437\u043d\u0430\u0447\u0435\u043d\u0438\u0435: {max}',
    pattern: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043a\u043e\u0440\u0440\u0435\u043a\u0442\u043d\u043e\u0435 \u0437\u043d\u0430\u0447\u0435\u043d\u0438\u0435',
    invalidZip: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u044b\u0439 \u043f\u043e\u0447\u0442\u043e\u0432\u044b\u0439 \u0438\u043d\u0434\u0435\u043a\u0441',
    invalidSSN: '\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u0442\u0435\u043b\u044c\u043d\u044b\u0439 \u043d\u043e\u043c\u0435\u0440 \u0441\u043e\u0446\u0438\u0430\u043b\u044c\u043d\u043e\u0433\u043e \u0441\u0442\u0440\u0430\u0445\u043e\u0432\u0430\u043d\u0438\u044f',
    checkThisField: '\u041f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u043f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u044d\u0442\u043e \u043f\u043e\u043b\u0435:',
    checkTheseFields: '\u041f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u043f\u0440\u043e\u0432\u0435\u0440\u044c\u0442\u0435 \u0432\u044b\u0434\u0435\u043b\u0435\u043d\u043d\u044b\u0435 \u043f\u043e\u043b\u044f:',

    // Common Field Labels (fallbacks)
    firstName: '\u0418\u043c\u044f',
    lastName: '\u0424\u0430\u043c\u0438\u043b\u0438\u044f',
    middleName: '\u041e\u0442\u0447\u0435\u0441\u0442\u0432\u043e',
    fullName: '\u041f\u043e\u043b\u043d\u043e\u0435 \u0438\u043c\u044f',
    email: '\u042d\u043b\u0435\u043a\u0442\u0440\u043e\u043d\u043d\u0430\u044f \u043f\u043e\u0447\u0442\u0430',
    phone: '\u0422\u0435\u043b\u0435\u0444\u043e\u043d',
    cellPhone: '\u041c\u043e\u0431\u0438\u043b\u044c\u043d\u044b\u0439 \u0442\u0435\u043b\u0435\u0444\u043e\u043d',
    homePhone: '\u0414\u043e\u043c\u0430\u0448\u043d\u0438\u0439 \u0442\u0435\u043b\u0435\u0444\u043e\u043d',
    workPhone: '\u0420\u0430\u0431\u043e\u0447\u0438\u0439 \u0442\u0435\u043b\u0435\u0444\u043e\u043d',
    address: '\u0410\u0434\u0440\u0435\u0441',
    address1: '\u0410\u0434\u0440\u0435\u0441, \u0441\u0442\u0440\u043e\u043a\u0430 1',
    address2: '\u0410\u0434\u0440\u0435\u0441, \u0441\u0442\u0440\u043e\u043a\u0430 2',
    city: '\u0413\u043e\u0440\u043e\u0434',
    state: '\u0428\u0442\u0430\u0442',
    zip: '\u041f\u043e\u0447\u0442\u043e\u0432\u044b\u0439 \u0438\u043d\u0434\u0435\u043a\u0441',
    zipCode: '\u041f\u043e\u0447\u0442\u043e\u0432\u044b\u0439 \u0438\u043d\u0434\u0435\u043a\u0441',
    country: '\u0421\u0442\u0440\u0430\u043d\u0430',
    dateOfBirth: '\u0414\u0430\u0442\u0430 \u0440\u043e\u0436\u0434\u0435\u043d\u0438\u044f',
    dob: '\u0414\u0430\u0442\u0430 \u0440\u043e\u0436\u0434\u0435\u043d\u0438\u044f',
    ssn: '\u041d\u043e\u043c\u0435\u0440 \u0441\u043e\u0446\u0438\u0430\u043b\u044c\u043d\u043e\u0433\u043e \u0441\u0442\u0440\u0430\u0445\u043e\u0432\u0430\u043d\u0438\u044f',
    gender: '\u041f\u043e\u043b',
    male: '\u041c\u0443\u0436\u0441\u043a\u043e\u0439',
    female: '\u0416\u0435\u043d\u0441\u043a\u0438\u0439',
    other: '\u0414\u0440\u0443\u0433\u043e\u0439',
    preferNotToSay: '\u041f\u0440\u0435\u0434\u043f\u043e\u0447\u0438\u0442\u0430\u044e \u043d\u0435 \u0433\u043e\u0432\u043e\u0440\u0438\u0442\u044c',
    signature: '\u041f\u043e\u0434\u043f\u0438\u0441\u044c',
    date: '\u0414\u0430\u0442\u0430',
    comments: '\u041a\u043e\u043c\u043c\u0435\u043d\u0442\u0430\u0440\u0438\u0438',
    notes: '\u0417\u0430\u043c\u0435\u0442\u043a\u0438',

    // Status Messages
    formSubmitted: '\u0424\u043e\u0440\u043c\u0430 \u0443\u0441\u043f\u0435\u0448\u043d\u043e \u043e\u0442\u043f\u0440\u0430\u0432\u043b\u0435\u043d\u0430',
    formSaved: '\u0424\u043e\u0440\u043c\u0430 \u0443\u0441\u043f\u0435\u0448\u043d\u043e \u0441\u043e\u0445\u0440\u0430\u043d\u0435\u043d\u0430',
    submissionError: '\u041e\u0448\u0438\u0431\u043a\u0430 \u043f\u0440\u0438 \u043e\u0442\u043f\u0440\u0430\u0432\u043a\u0435 \u0444\u043e\u0440\u043c\u044b. \u041f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u043f\u043e\u043f\u0440\u043e\u0431\u0443\u0439\u0442\u0435 \u0441\u043d\u043e\u0432\u0430.',
    loadingForm: '\u0417\u0430\u0433\u0440\u0443\u0437\u043a\u0430 \u0444\u043e\u0440\u043c\u044b...',
    pleaseWait: '\u041f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u043f\u043e\u0434\u043e\u0436\u0434\u0438\u0442\u0435...',

    // Signature
    signHere: '\u041f\u043e\u0434\u043f\u0438\u0448\u0438\u0442\u0435 \u0437\u0434\u0435\u0441\u044c',
    clearSignature: '\u041e\u0447\u0438\u0441\u0442\u0438\u0442\u044c \u043f\u043e\u0434\u043f\u0438\u0441\u044c',
    signatureRequired: '\u041f\u043e\u0434\u043f\u0438\u0441\u044c \u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u0430',

    // File Upload
    fileUploaded: '\u0424\u0430\u0439\u043b \u0443\u0441\u043f\u0435\u0448\u043d\u043e \u0437\u0430\u0433\u0440\u0443\u0436\u0435\u043d',
    fileUploadError: '\u041e\u0448\u0438\u0431\u043a\u0430 \u0437\u0430\u0433\u0440\u0443\u0437\u043a\u0438 \u0444\u0430\u0439\u043b\u0430',
    fileTooLarge: '\u0424\u0430\u0439\u043b \u0441\u043b\u0438\u0448\u043a\u043e\u043c \u0431\u043e\u043b\u044c\u0448\u043e\u0439',
    invalidFileType: '\u041d\u0435\u0434\u043e\u043f\u0443\u0441\u0442\u0438\u043c\u044b\u0439 \u0442\u0438\u043f \u0444\u0430\u0439\u043b\u0430',
    maxFileSize: '\u041c\u0430\u043a\u0441\u0438\u043c\u0430\u043b\u044c\u043d\u044b\u0439 \u0440\u0430\u0437\u043c\u0435\u0440 \u0444\u0430\u0439\u043b\u0430: {size}',

    // Yes/No
    yes: '\u0414\u0430',
    no: '\u041d\u0435\u0442',

    // Language
    language: '\u042f\u0437\u044b\u043a',
    english: '\u0410\u043d\u0433\u043b\u0438\u0439\u0441\u043a\u0438\u0439',
    spanish: '\u0418\u0441\u043f\u0430\u043d\u0441\u043a\u0438\u0439',

    // Form packets
    saveAndContinue: '\u0421\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c \u0438 \u043f\u0440\u043e\u0434\u043e\u043b\u0436\u0438\u0442\u044c',
    sectionOf: '\u0420\u0430\u0437\u0434\u0435\u043b {step} \u0438\u0437 {total}',
    lastSection: '\u043f\u043e\u0441\u043b\u0435\u0434\u043d\u0438\u0439',
    packetCompleteTitle: '\u0412\u044b \u0443\u0436\u0435 \u0437\u0430\u043f\u043e\u043b\u043d\u0438\u043b\u0438 \u044d\u0442\u043e\u0442 \u043f\u0430\u043a\u0435\u0442',
    packetCompleteMessage: '\u0412\u0441\u0435 \u0440\u0430\u0437\u0434\u0435\u043b\u044b \u043e\u0442\u043f\u0440\u0430\u0432\u043b\u0435\u043d\u044b. \u0417\u0430\u043f\u043e\u043b\u043d\u044f\u0442\u044c \u0431\u043e\u043b\u044c\u0448\u0435 \u043d\u0435\u0447\u0435\u0433\u043e.',
    packetCompleteHint: '\u0415\u0441\u043b\u0438 \u043d\u0443\u0436\u043d\u043e \u0447\u0442\u043e-\u0442\u043e \u0438\u0437\u043c\u0435\u043d\u0438\u0442\u044c, \u043f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u0441\u0432\u044f\u0436\u0438\u0442\u0435\u0441\u044c \u0441 \u043e\u0444\u0438\u0441\u043e\u043c.',

    // Break points inside one form
    continue: '\u041f\u0440\u043e\u0434\u043e\u043b\u0436\u0438\u0442\u044c',
    partOf: '\u0427\u0430\u0441\u0442\u044c {step} \u0438\u0437 {total}',
    progressSaveFailed: '\u041d\u0435 \u0443\u0434\u0430\u043b\u043e\u0441\u044c \u0441\u043e\u0445\u0440\u0430\u043d\u0438\u0442\u044c \u0432\u0430\u0448 \u043f\u0440\u043e\u0433\u0440\u0435\u0441\u0441, \u043d\u043e \u0432\u044b \u043c\u043e\u0436\u0435\u0442\u0435 \u043f\u0440\u043e\u0434\u043e\u043b\u0436\u0438\u0442\u044c.',

    // Health history by category
    whichApply: '\u0447\u0442\u043e \u0438\u0437 \u044d\u0442\u043e\u0433\u043e \u043e\u0442\u043d\u043e\u0441\u0438\u0442\u0441\u044f \u043a \u0432\u0430\u043c? \u041e\u0442\u043c\u0435\u0442\u044c\u0442\u0435 \u0432\u0441\u0435 \u043f\u043e\u0434\u0445\u043e\u0434\u044f\u0449\u0435\u0435.',
    alsoOnFile: '\u0422\u0430\u043a\u0436\u0435 \u0432 \u043a\u0430\u0440\u0442\u0435 (\u043d\u0435 \u043f\u043e\u043a\u0430\u0437\u044b\u0432\u0430\u0435\u0442\u0441\u044f \u043f\u0430\u0446\u0438\u0435\u043d\u0442\u0443)',
    noConditions: '\u041d\u0435\u0442 \u0437\u0430\u0431\u043e\u043b\u0435\u0432\u0430\u043d\u0438\u0439',
    describeOther: '\u041e\u043f\u0438\u0448\u0438\u0442\u0435, \u043f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u0432\u0430\u0448\u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u0437\u0430\u0431\u043e\u043b\u0435\u0432\u0430\u043d\u0438\u044f',
    describeOtherOptional: '\u0414\u0440\u0443\u0433\u0438\u0435 \u0437\u0430\u0431\u043e\u043b\u0435\u0432\u0430\u043d\u0438\u044f, \u043a\u043e\u0442\u043e\u0440\u044b\u0445 \u043d\u0435\u0442 \u0432 \u0441\u043f\u0438\u0441\u043a\u0435 \u0432\u044b\u0448\u0435 (\u043d\u0435\u043e\u0431\u044f\u0437\u0430\u0442\u0435\u043b\u044c\u043d\u043e)',
    describeOtherPlaceholder: '\u0423\u043a\u0430\u0436\u0438\u0442\u0435 \u0437\u0430\u0431\u043e\u043b\u0435\u0432\u0430\u043d\u0438\u044f, \u043a\u043e\u0442\u043e\u0440\u044b\u0445 \u043d\u0435\u0442 \u0432 \u0441\u043f\u0438\u0441\u043a\u0435 \u0432\u044b\u0448\u0435',
    describeOtherRequired: '\u041f\u043e\u0436\u0430\u043b\u0443\u0439\u0441\u0442\u0430, \u043e\u043f\u0438\u0448\u0438\u0442\u0435 \u0432\u0430\u0448\u0438 \u0434\u0440\u0443\u0433\u0438\u0435 \u0437\u0430\u0431\u043e\u043b\u0435\u0432\u0430\u043d\u0438\u044f, \u043f\u0440\u0435\u0436\u0434\u0435 \u0447\u0435\u043c \u043f\u0440\u043e\u0434\u043e\u043b\u0436\u0438\u0442\u044c.'
  }
};

/**
 * Get a system translation
 *
 * @param {string} key - Translation key
 * @param {string} language - Language code ('en', 'es', 'ru', etc.)
 * @param {Object} params - Optional parameters for interpolation
 * @returns {string} - Translated string (falls back to English if key not found in target language)
 *
 * @example
 * t('submit', 'es') // Returns 'Enviar'
 * t('submit', 'ru') // Returns 'Отправить'
 * t('minLength', 'en', { min: 5 }) // Returns 'Must be at least 5 characters'
 */
export function t(key, language = 'en', params = {}) {
  const lang = systemTranslations[language] || systemTranslations.en;
  let text = lang[key] || systemTranslations.en[key] || key;

  // Interpolate parameters
  if (params && Object.keys(params).length > 0) {
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
    });
  }

  return text;
}

/**
 * Get validation error message in the specified language
 *
 * @param {string} ruleType - Validation rule type (e.g., 'REQUIRED', 'EMAIL')
 * @param {string} language - Language code
 * @param {Object} params - Optional parameters (e.g., { min: 5 })
 * @returns {string} - Localized error message
 */
export function getValidationMessage(ruleType, language = 'en', params = {}) {
  const ruleMap = {
    'REQUIRED': 'required',
    'EMAIL': 'invalidEmail',
    'PHONE': 'invalidPhone',
    'DATE': 'invalidDate',
    'NUMBER': 'invalidNumber',
    'MIN_LENGTH': 'minLength',
    'MAX_LENGTH': 'maxLength',
    'MIN': 'minValue',
    'MAX': 'maxValue',
    'PATTERN': 'pattern',
    'REGEX': 'pattern',
    'ZIP': 'invalidZip',
    'SSN': 'invalidSSN'
  };

  const key = ruleMap[ruleType.toUpperCase()] || 'pattern';
  return t(key, language, params);
}

export default {
  systemTranslations,
  t,
  getValidationMessage
};
